// Uses LOCAL calendar parts — toISOString() converts to UTC and shifts the date for UTC+ users.
export const toLocalDateStr = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;


export const fmtLocalDate = (val) => {
  if (!val) return null;
  try {
    const datePart = String(val).slice(0, 10);
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    }).format(new Date(`${datePart}T00:00:00`));
  } catch {
    return String(val);
  }
};

// Today's business day as "yyyy-MM-dd". Must be US Eastern, not the browser's zone: the API and the database split
// an ad's run dates into history / upcoming at Eastern midnight, and the CMS has to draw the same line.
export const todayEastern = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date());

// An ad has STARTED running when it was approved and has a run date on or before today. A draft or rejected ad has never
// run, whatever dates it holds. (A status we were not given is treated as approved: the safe side, i.e. locked.)
export const adHasStarted = (ad, today = todayEastern()) =>
  Boolean(ad)
  && (ad.contentStatus ?? 'approved') === 'approved'
  && (ad.scheduleDates ?? []).some((d) => String(d).slice(0, 10) <= today);

// "2026-10-15" + 1 -> "2026-10-16". Whole days in UTC, so it is exact across daylight-saving changes.
export const addDays = (iso, n) => {
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}/;
const cleanDates = (dates) =>
  [...new Set((Array.isArray(dates) ? dates : []).filter((d) => DATE_ONLY.test(String(d))).map((d) => String(d).slice(0, 10)))].sort();

// Whole-day index in UTC, so "next day" is exact across daylight-saving changes.
const dayIndex = (d) => {
  const [y, m, day] = d.split('-').map(Number);
  return Date.UTC(y, m - 1, day) / 86400000;
};

// ["2026-10-01","2026-10-02","2026-10-05"] -> [{ start:"2026-10-01", end:"2026-10-02", count:2 }, { start:"2026-10-05", end:"2026-10-05", count:1 }]
export const compressDateRanges = (dates) => {
  const ranges = [];
  for (const d of cleanDates(dates)) {
    const last = ranges[ranges.length - 1];
    if (last && dayIndex(d) - dayIndex(last.end) === 1) { last.end = d; last.count += 1; }
    else ranges.push({ start: d, end: d, count: 1 });
  }
  return ranges;
};

// "01 Oct 2026 – 30 Oct 2026" / "01 Oct 2026 – 02 Oct 2026, 05 Oct 2026 +1 more" — consecutive days collapse into one range.
export const formatDateRanges = (dates, max = 3) => {
  const ranges = compressDateRanges(dates);
  if (ranges.length === 0) return '';
  const fmt = (r) => (r.start === r.end ? fmtLocalDate(r.start) : `${fmtLocalDate(r.start)} – ${fmtLocalDate(r.end)}`);
  const shown = ranges.slice(0, max).map(fmt);
  return ranges.length > max ? `${shown.join(', ')} +${ranges.length - max} more` : shown.join(', ');
};

// Sorts an ad's stored run dates against its campaign's window:
//   current  inside [startDate, endDate]: what the ad runs on now (the picker edits these; past ones are locked)
//   history  before today and outside the window: already-past run dates of an earlier window; kept, read-only
//   stale    today or later and outside the window: can never run in this campaign; dropped when the ad is saved
export const splitRunDates = (dates, startDate, endDate, today = todayEastern()) => {
  const list  = cleanDates(dates);
  const start = startDate ? String(startDate).slice(0, 10) : null;
  const end   = endDate   ? String(endDate).slice(0, 10)   : null;
  if (!start || !end) return { current: list, history: [], stale: [] };

  const out = { current: [], history: [], stale: [] };
  for (const d of list) {
    if (d >= start && d <= end) out.current.push(d);
    else if (d < today)         out.history.push(d);
    else                        out.stale.push(d);
  }
  return out;
};

// "03 Sep 2026, 04 Sep 2026, 10 Sep 2026 +2 more" — a compact list for ["yyyy-MM-dd", ...].
export const summarizeDates = (dates, max = 6) => {
  const list = Array.isArray(dates) ? dates : [];
  if (list.length === 0) return '';
  const shown = list.slice(0, max).map((d) => fmtLocalDate(d) ?? String(d));
  return list.length > max ? `${shown.join(', ')} +${list.length - max} more` : shown.join(', ');
};
